import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import SideNavBar from './SideNavBar';

const AddEquipment = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef();

  // Close sidebar when clicking outside (mobile)
  useEffect(() => {
    if (!sidebarOpen) return;
    function handleClick(e) {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setSidebarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sidebarOpen]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [darkMode]);

  return (
    <div
      className={darkMode ? 'dark bg-gray-900 min-h-screen h-full' : 'bg-white min-h-screen h-full'}
    >
      {/* Dark mode toggle */}
      <button
        className={`fixed top-4 right-4 z-50 border-2 border-green-700 rounded-full p-2 shadow-lg transition hover:bg-green-700 hover:text-white
           ${darkMode ? 'bg-gray-800 text-yellow-400' : 'bg-white text-green-800'}`}
        onClick={() => setDarkMode(!darkMode)}
        aria-label="Toggle dark mode"
      >
        {darkMode ? (
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 4.95l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"
            />
          </svg>
        )}
      </button>

      {/* Hamburger for mobile */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-white text-black shadow-lg transition-colors duration-300 hover:bg-green-700 hover:text-white"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open sidebar"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <div className="flex h-screen">
        {/* Desktop Sidebar */}
        <div
          className={`hidden md:block fixed top-0 left-0 h-screen w-52 overflow-y-auto bg-gradient-to-r ${
            darkMode ? 'from-gray-900 to-gray-800' : 'from-green-800 to-yellow-200'
          } p-3 font-bold custom-scrollbar z-50`}
        >
          <SideNavBar darkMode={darkMode} />
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && <div className="fixed inset-0 z-40 bg-black bg-opacity-40 md:hidden" />}

        {/* Mobile Sidebar */}
        <div
          ref={sidebarRef}
          className={`fixed top-0 left-0 z-50 w-64 h-screen bg-gradient-to-r ${
            darkMode ? 'from-gray-900 to-gray-800' : 'from-green-800 to-yellow-200'
          } p-3 font-bold custom-scrollbar transform transition-transform duration-300 md:hidden ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex justify-between items-center mb-4">
            <span className="font-bold text-lg text-white">Menu</span>
            <button
              className="p-2 rounded-md bg-gray-200 dark:bg-gray-100"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <SideNavBar darkMode={darkMode} />
        </div>
        {/* Main Content */}
        <div
          className={`flex-1 p-6 md:ml-52  transition-all duration-300 ${
            darkMode ? 'dark:bg-gray-900' : 'bg-white'
          }`}
        >
          <h1 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Add New Equipment
          </h1>
          <form className="space-y-4">
            <div>
              <label
                className={`block text-sm font-medium  ${
                  darkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Equipment Name
              </label>
              <input
                type="text"
                className={`mt-1 block w-full p-2 border border-gray-300 rounded-md ${
                  darkMode ? 'dark:bg-gray-800 dark:border-gray-700 dark:text-white' : ''
                }`}
                placeholder="Enter equipment name"
              />
            </div>
            <div>
              <label
                className={`block text-sm font-medium  ${
                  darkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Category
              </label>
              <select
                className={`mt-1 block w-full p-2 border border-gray-300 rounded-md ${
                  darkMode ? 'dark:bg-gray-800 dark:border-gray-700 dark:text-white' : ''
                }`}
              >
                <option>Media</option>
                <option>Sound</option>
                <option>Lighting</option>
                <option>Musical</option>
              </select>
            </div>
            <div>
              <label
                className={`block text-sm font-medium  ${
                  darkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Condition
              </label>
              <select
                className={`mt-1 block w-full p-2 border border-gray-300 rounded-md ${
                  darkMode ? 'dark:bg-gray-800 dark:border-gray-700 dark:text-white' : ''
                }`}
              >
                <option>Excellent</option>
                <option>Good</option>
                <option>Need Attention</option>
              </select>
            </div>
            <div>
              <label
                className={`block text-sm font-medium  ${
                  darkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Location
              </label>
              <input
                type="text"
                className={`mt-1 block w-full p-2 border border-gray-300 rounded-md ${
                  darkMode ? 'dark:bg-gray-800 dark:border-gray-700 dark:text-white' : ''
                }`}
                placeholder="Enter location"
              />
            </div>
            <div>
              <label
                className={`block text-sm font-medium  ${
                  darkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Description
              </label>
              <textarea
                type="text"
                className={`mt-1 block w-full p-2 border border-gray-300 rounded-md ${
                  darkMode ? 'dark:bg-gray-800 dark:border-gray-700 dark:text-white' : ''
                }`}
                placeholder="Enter Short Description"
              />
            </div>
            <button
              type="submit"
              className={`w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md transition-colors duration-200 ${
                darkMode ? 'dark:bg-green-500' : ''
              }`}
            >
              Add Equipment
            </button>
          </form>
          <div className="mt-6">
            <NavLink
              to="/equipmentinventory"
              className={`w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md transition-colors duration-200 ${
                darkMode ? 'dark:bg-blue-500' : ''
              }`}
            >
              View Equipment Inventory
            </NavLink>
          </div>
        </div>
        <footer className="mt-10 text-center text-xs text-gray-400">
          &copy; 2025 All rights reserved. Church Management System
        </footer>
      </div>
    </div>
  );
};

export default AddEquipment;
